<template>
  <div class="overflow-hidden bg-white py-32">
    <div class="mx-auto max-w-7xl px-6 lg:flex lg:px-8">
      <div class="mx-auto grid max-w-2xl grid-cols-1 gap-x-12 gap-y-16 lg:mx-0 lg:min-w-full lg:max-w-none lg:grid-cols-[minmax(0,1fr)_minmax(24rem,32rem)]">
        <div class="lg:col-end-1 lg:w-full lg:max-w-lg lg:pb-8">
          <h2 class="text-3xl font-bold tracking-tight text-left text-gray-900 sm:text-4xl">Teaching at PVIS</h2>
          <p class="mt-6 text-xl font-semibold text-left leading-8 text-gray-900">Opportunities for 2026 and 2027</p>
          <div class="mt-6 space-y-5 text-base text-left leading-7 text-gray-600">
            <p>
              PVIS is now welcoming expressions of interest from qualified and passionate teachers to join our vibrant and dynamic team. We are seeking both primary and secondary teachers across a range of subject areas.
            </p>
            <p>
              We have openings for full-time, part-time and flexible casual employment. We are particularly interested in primary teachers; teachers of English, physics, art and humanities for Years 7-12; and French and physical education teachers who are able to teach across both primary and secondary year levels.
            </p>
            <p>
              Secondary teachers must be flexible and willing to teach across more than one subject area, according to the needs of the school.
            </p>
            <p>
              Some positions are available for an immediate start, by negotiation, or for the 2027 school year.
            </p>
          </div>

          <div class="mt-8 flex flex-wrap gap-4">
            <a
              href="https://canva.link/g3qbpj2cqdok0oa"
              target="_blank"
              rel="noopener noreferrer"
              class="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Find out more about working at PVIS
            </a>
          </div>

          <p v-if="content.qualificationsTitle" class="mt-6 font-bold text-base text-left leading-7 text-gray-600">{{ content.qualificationsTitle }}</p>

          <div class="mt-6 text-base text-left leading-7 text-gray-600 rich-text-content" v-if="content.qualifications" v-html="renderRichText(content.qualifications)"></div>

          <p class="mt-6 text-base text-left leading-7 text-gray-600">Please get in touch if you are available for work and are a qualified teacher: employment@pvis.edu.vu </p>
          <div class="mt-10 flex">
            <a href="mailto:employment@pvis.edu.vu?subject=Join Our Team" class="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Join our team <span aria-hidden="true">&rarr;</span></a>
          </div>

        </div>

        <div class="lg:col-start-2">
          <div class="grid grid-cols-1 gap-6 lg:sticky lg:top-28">
            <img
              v-for="poster in hiringPosters"
              :key="poster.src"
              :src="poster.src"
              :alt="poster.alt"
              class="w-full rounded-lg bg-gray-50 object-cover shadow-lg"
            />
          </div>
        </div>
       
        
      </div>

    </div>
  </div>
</template>



<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import primaryTeachersPoster from '@/assets/images/employment/primary-teachers-2026-2027.jpg';
import secondaryTeachersPoster from '@/assets/images/employment/secondary-teachers-2026-2027.jpg';
import specialistsPoster from '@/assets/images/employment/specialists-2026-2027.jpg';
import schoolCounsellorPoster from '@/assets/images/employment/school-counsellor-2027.jpg';

const content = ref({
  qualificationsTitle: '',
  qualifications: null
});

const hiringPosters = [
  {
    src: primaryTeachersPoster,
    alt: 'PVIS is hiring primary teachers'
  },
  {
    src: secondaryTeachersPoster,
    alt: 'PVIS is hiring secondary teachers'
  },
  {
    src: specialistsPoster,
    alt: 'PVIS is hiring K-12 specialists'
  },
  {
    src: schoolCounsellorPoster,
    alt: 'PVIS is hiring a school counsellor'
  }
];

const renderRichText = (richTextContent) => {
  if (!richTextContent) return '';
  return documentToHtmlString(richTextContent);
};

const fetchContent = async () => {
  // Directly use the VUE_APP_ prefixed variable, which Vue CLI makes available
  const accessToken = process.env.VUE_APP_CONTENTFUL_ACCESS_TOKEN;

  if (!accessToken) {
    console.error('Contentful access token is missing for employment page. Please check your environment variables.');
    // Optionally handle the error state for the UI
    return; // Stop execution if the token is missing
  }

  try {
    const response = await axios.get(
      `https://cdn.contentful.com/spaces/8ayqhzh3p1ua/environments/master/entries?access_token=${accessToken}&content_type=employment`
    );
    

    if (response.data.items && response.data.items.length > 0) {
      const fields = response.data.items[0].fields;

      content.value = {
        qualificationsTitle: fields.qualificationsTitle || '',
        qualifications: fields.qualifications || null
      };

    } else {
      console.error('No content found in Contentful');
    }
  } catch (error) {
    console.error('Error fetching content from Contentful:', error);
  }
};

onMounted(() => {
  fetchContent();
});
</script>

<style>
.rich-text-content p {
  margin-bottom: 1rem;
}

.rich-text-content ul {
  margin: 1rem 0;
  padding-left: 1.5rem;
  list-style-type: disc;
}

.rich-text-content ol {
  margin: 1rem 0;
  padding-left: 1.5rem;
  list-style-type: decimal;
}

.rich-text-content ul ul {
  list-style-type: circle;
}

.rich-text-content ul ul ul {
  list-style-type: square;
}

.rich-text-content li {
  margin-bottom: 0.5rem;
  padding-left: 0.5rem;
}

.rich-text-content h1,
.rich-text-content h2,
.rich-text-content h3,
.rich-text-content h4,
.rich-text-content h5,
.rich-text-content h6 {
  margin: 1.5rem 0 1rem 0;
  font-weight: bold;
}

.rich-text-content blockquote {
  border-left: 4px solid #e5e7eb;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
}

.rich-text-content a {
  color: #4f46e5;
  text-decoration: underline;
}

.rich-text-content a:hover {
  color: #4338ca;
}
</style>
